import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  LANGUAGE_LABELS,
  SUPPORTED_LANGUAGES,
  currentLanguage,
  resolvePublicLanguage,
  setAppLanguage,
  type AppLanguage,
} from "../i18n";

import batteryImg from "../assets/app-guide/peeka-battery-level-08-front.png";
import cardImg from "../assets/app-guide/peeka-card-front.png";
import graphImg from "../assets/app-guide/peeka-graph-up-front.png";
import wordmarkImg from "../assets/app-guide/peeka-wordmark-navy.png";
import { playStoreUrl } from "../lib/openInApp";
import styles from "./AppGuidePage.module.css";

/**
 * 앱 안내(소개) 페이지 — 디자인 정본: codex/peeka-app-guide/index.html (2026-09-15).
 *
 * 노출 경로는 둘이다(main.tsx):
 *   - 안내 호스트(VITE_LANDING_HOST = app.peeka.app / app.dev.peeka.app) — 상시
 *   - 웹 비공개 기간(VITE_WEB_PUBLIC !== "true")의 공개 약관 외 전 경로
 * 네이티브 앱 / OTA 에는 절대 노출되지 않는다(main.tsx 의 isNative 가드).
 *
 * 표시 언어: `?lang=ko|en|ja` > 앱 언어(저장값 > 기기 언어 > en). 공개 약관과 같은 규칙이다
 * (i18n/resolvePublicLanguage). 스토어·광고에 나라별 링크를 걸 수 있어야 해서 URL 이 이긴다.
 *
 * ⚠ 스토어 버튼은 스토어별로 "출시 예정" 비활성 여부를 따로 둔다. Google Play 는
 *   2026-10 공개돼 활성, App Store 는 심사 통과 전이라 비활성 — 통과하면
 *   APP_STORE_COMING_SOON 만 내리면 된다(문구 키는 storeReady 로 이미 있다).
 *   종전 ComingSoonPage 는 여기를 링크로 걸어 뒀는데, 스토어 레코드가 없어
 *   양쪽 다 404 로 떨어졌다.
 */
const APP_STORE_COMING_SOON = true;
const PLAY_STORE_COMING_SOON = false;

/** Play Console 획득 보고서가 이 페이지에서 온 설치를 따로 세도록 install referrer 에 UTM 을 싣는다. */
const PLAY_STORE_HREF = playStoreUrl("utm_source=peeka_web&utm_medium=app_guide");

/** 스크롤 등장 — 디자인의 IntersectionObserver(threshold .12) 그대로. */
function useReveal() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    // jsdom/구형 브라우저 폴백 — 관찰자가 없으면 처음부터 보이게 둔다.
    if (!el || typeof IntersectionObserver === "undefined") return;

    const targets = el.querySelectorAll(`.${styles.reveal}`);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealVisible);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12 },
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  return root;
}

function AppStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.05 12.54c-.03-3.15 2.57-4.68 2.69-4.75a5.77 5.77 0 0 0-4.54-2.46c-1.91-.2-3.76 1.14-4.74 1.14-1 0-2.51-1.12-4.14-1.08A6.02 6.02 0 0 0 1.25 8.5c-2.2 3.82-.56 9.44 1.55 12.53 1.06 1.51 2.29 3.2 3.91 3.14 1.59-.07 2.18-1 4.1-1 1.9 0 2.46 1 4.12.96 1.7-.03 2.77-1.52 3.79-3.04a12.5 12.5 0 0 0 1.73-3.52 5.44 5.44 0 0 1-3.4-5.03ZM13.95 3.3A5.5 5.5 0 0 0 15.2-.66a5.64 5.64 0 0 0-3.65 1.88 5.2 5.2 0 0 0-1.28 3.8 4.67 4.67 0 0 0 3.68-1.72Z" />
    </svg>
  );
}

function PlayStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m3 2 12.5 10L3 22V2Zm13.8 8.9 3.3-2c.7-.4 1.5.1 1.5.9v4.4c0 .8-.8 1.3-1.5.9l-3.3-2.1 1.3-1-1.3-1.1Z" />
    </svg>
  );
}

interface StoreLinkProps {
  href: string;
  name: string;
  label: string;
  icon: React.ReactNode;
  coming: boolean;
}

function StoreLink({ href, name, label, icon, coming }: StoreLinkProps) {
  return (
    <a
      className={`${styles.store} ${coming ? styles.storeComing : ""}`}
      href={coming ? undefined : href}
      aria-disabled={coming || undefined}
      target={coming ? undefined : "_blank"}
      rel={coming ? undefined : "noopener noreferrer"}
      aria-label={`${name} — ${label}`}
    >
      {icon}
      <span>
        <small>{name}</small>
        <strong>{label}</strong>
      </span>
    </a>
  );
}

export default function AppGuidePage() {
  const { i18n } = useTranslation(["app", "common"]);
  const root = useReveal();
  const [searchParams, setSearchParams] = useSearchParams();

  /* i18n.language 를 의존성에 둬야 앱 언어를 바꿨을 때(=?lang= 없는 경우) 다시 계산된다. */
  const lang = useMemo(
    () => resolvePublicLanguage(searchParams.get("lang"), currentLanguage()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, i18n.language],
  );

  /* 전역 t 가 아니라 **언어를 고정한 t** 를 쓴다 — ?lang=ja 링크를 열었다고 사용자의
     저장된 언어까지 바뀌면 안 되기 때문이다(공개 약관과 같은 원칙). */
  const t = useMemo(() => i18n.getFixedT(lang, ["app", "common"]), [i18n, lang]);
  const storeLabel = (coming: boolean) => t(coming ? "appGuide.storeComing" : "appGuide.storeReady");

  /* 이 화면은 검색엔진·스토어 심사가 직접 여는 공개 페이지라 <html lang> 이 실제
     표시 언어와 같아야 한다(index.html 은 ko 로 고정돼 있다). 앱 화면은 종전대로. */
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  /* 버튼으로 고르면 ① 앱 언어로 저장하고 ② URL 도 함께 바꾼다 — 바꾼 화면 그대로
     주소를 복사해 공유할 수 있어야 하고, ?lang= 이 남아 있으면 그쪽이 이기기 때문이다. */
  const selectLanguage = useCallback(
    (code: AppLanguage) => {
      void setAppLanguage(code);
      const next = new URLSearchParams(searchParams);
      next.set("lang", code);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return (
    <div className={styles.page} ref={root}>
      <div className={styles.noise} aria-hidden="true" />

      <header className={`${styles.wrap} ${styles.nav}`}>
        <a href="#top" aria-label={t("appGuide.homeAria")}>
          <img className={styles.logo} src={wordmarkImg} alt={t("common:brand.name")} />
        </a>
        <a className={styles.navCta} href="#download">
          {t("appGuide.navCta")}
        </a>
      </header>

      <main>
        <section className={`${styles.wrap} ${styles.hero}`} id="top">
          <div className={styles.reveal}>
            <p className={styles.eyebrow}>YOUR CARDS, CLEARLY VALUED</p>
            <h1 className={styles.title}>
              {t("appGuide.titleLead")}
              <br />
              <em>{t("appGuide.titleEm")}</em>
            </h1>
            <p className={styles.heroLead}>{t("appGuide.lead")}</p>
            <div className={styles.heroActions}>
              <a className={styles.primaryCta} href="#download">
                {t("appGuide.ctaPrimary")} <span aria-hidden="true">↗</span>
              </a>
              <a className={styles.textLink} href="#features">
                {t("appGuide.ctaSecondary")}
              </a>
            </div>
            <p className={styles.micro}>{t("appGuide.micro")}</p>
          </div>

          <div className={`${styles.heroArt} ${styles.reveal}`} aria-label={t("appGuide.artAria")}>
            <div className={styles.burst} />
            <div className={`${styles.floatTag} ${styles.tagA}`}>SCAN → CHECK</div>
            <div className={`${styles.floatTag} ${styles.tagB}`}>COLLECT YOUR VALUE</div>
            <div className={styles.phone}>
              <div className={styles.screen}>
                <span className={styles.screenLabel}>PEEKA SCAN</span>
                <h3 className={styles.screenTitle}>{t("appGuide.phoneTitle")}</h3>
                <div className={styles.scanCard}>
                  <img src={cardImg} alt={t("appGuide.scanAlt")} />
                  <i className={styles.scanLine} />
                </div>
                <div className={styles.priceBox}>
                  <span>
                    <small>{t("appGuide.phonePriceLabel")}</small>
                    <strong>₩ 128,000</strong>
                  </span>
                  <span className={styles.up}>▲ 8.4%</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className={styles.marquee} aria-hidden="true">
          <div className={styles.marqueeTrack}>
            <span>
              SCAN YOUR CARD <b>✦</b> CHECK THE PRICE <b>✦</b> BUILD YOUR COLLECTION <b>✦</b>
            </span>
            <span>
              SCAN YOUR CARD <b>✦</b> CHECK THE PRICE <b>✦</b> BUILD YOUR COLLECTION <b>✦</b>
            </span>
          </div>
        </div>

        <section className={`${styles.wrap} ${styles.section}`} id="features">
          <div className={`${styles.sectionHead} ${styles.reveal}`}>
            <div>
              <p className={styles.sectionKicker}>THREE THINGS. ONE APP.</p>
              <h2 className={styles.sectionTitle}>{t("appGuide.featuresTitle")}</h2>
            </div>
            <p className={styles.sectionDesc}>{t("appGuide.featuresDesc")}</p>
          </div>
          <div className={styles.features}>
            <article className={`${styles.feature} ${styles.reveal}`}>
              <span className={styles.featureNo}>01 / SCAN</span>
              <img src={cardImg} alt={t("appGuide.f1.title")} />
              <h3>{t("appGuide.f1.title")}</h3>
              <p>{t("appGuide.f1.desc")}</p>
            </article>
            <article className={`${styles.feature} ${styles.reveal}`}>
              <span className={styles.featureNo}>02 / VALUE</span>
              <img src={graphImg} alt={t("appGuide.f2.title")} />
              <h3>{t("appGuide.f2.title")}</h3>
              <p>{t("appGuide.f2.desc")}</p>
            </article>
            <article className={`${styles.feature} ${styles.reveal}`}>
              <span className={styles.featureNo}>03 / COLLECT</span>
              <img src={batteryImg} alt={t("appGuide.f3.title")} />
              <h3>{t("appGuide.f3.title")}</h3>
              <p>{t("appGuide.f3.desc")}</p>
            </article>
          </div>
        </section>

        <section className={`${styles.stepsSection} ${styles.section}`}>
          <div className={styles.wrap}>
            <div className={`${styles.sectionHead} ${styles.reveal}`}>
              <div>
                <p className={styles.sectionKicker}>HOW IT WORKS</p>
                <h2 className={styles.sectionTitle}>{t("appGuide.stepsTitle")}</h2>
              </div>
            </div>
            <div className={styles.steps}>
              {([1, 2, 3, 4] as const).map((n) => (
                <article className={`${styles.step} ${styles.reveal}`} key={n}>
                  <b>{`0${n}`}</b>
                  <h3>{t(`appGuide.s${n}.title`)}</h3>
                  <p>{t(`appGuide.s${n}.desc`)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={`${styles.wrap} ${styles.download}`} id="download">
          <div className={`${styles.downloadBox} ${styles.reveal}`}>
            <div>
              <p className={styles.sectionKicker}>READY TO PEEK?</p>
              <h2 className={styles.sectionTitle}>{t("appGuide.downloadTitle")}</h2>
              <p className={styles.downloadNote}>{t("appGuide.downloadNote")}</p>
            </div>
            <div className={styles.storeButtons}>
              <StoreLink
                href="https://apps.apple.com/app/id6796612284"
                name="App Store"
                label={storeLabel(APP_STORE_COMING_SOON)}
                icon={<AppStoreIcon />}
                coming={APP_STORE_COMING_SOON}
              />
              <StoreLink
                href={PLAY_STORE_HREF}
                name="Google Play"
                label={storeLabel(PLAY_STORE_COMING_SOON)}
                icon={<PlayStoreIcon />}
                coming={PLAY_STORE_COMING_SOON}
              />
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={`${styles.wrap} ${styles.footerRow}`}>
          <img src={wordmarkImg} alt={t("common:brand.name")} />
          <span>© {new Date().getFullYear()} peeka. All rights reserved.</span>
          <span>{t("appGuide.tagline")}</span>
          {/* 언어 전환 — 기본은 기기 언어를 따르지만(i18n initialLanguage), 이 페이지는
              한·일·미에 함께 나가는 공개 주소라 방문자가 직접 고를 수 있어야 한다.
              선택은 앱과 같은 저장소(localStorage app.language)를 쓴다. */}
          <div className={styles.langs} role="group" aria-label={t("appGuide.langAria")}>
            {SUPPORTED_LANGUAGES.map((code) => (
              <button
                key={code}
                type="button"
                className={code === lang ? styles.langActive : styles.lang}
                aria-current={code === lang || undefined}
                onClick={() => selectLanguage(code)}
              >
                {LANGUAGE_LABELS[code]}
              </button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
